import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import { format } from "date-fns";

// Template type constants
const TEMPLATE_TYPES = [
    { key: "B2C", label: "B2C Websites", icon: "ri-store-line", color: "primary" },
    { key: "B2B", label: "B2B Websites", icon: "ri-building-line", color: "success" },
    { key: "B2E", label: "B2E Websites", icon: "ri-group-line", color: "info" },
    { key: "Travel", label: "Travel Websites", icon: "ri-earth-line", color: "warning" }
];

const WebsiteTemplates = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [activeType, setActiveType] = useState("B2C");
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        templateType: "B2C",
        template: "",
        thumbnail: null
    });
    const [formErrors, setFormErrors] = useState({});
    const [btnLoading, setBtnLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    // Fetch templates when activeType changes
    useEffect(() => {
        if (user && !loading) {
            fetchTemplatesByType(activeType);
        }
    }, [user, loading, activeType]);

    const fetchTemplatesByType = async (type) => {
        setIsLoading(true);
        try {
            const response = await apiService.getSingle(`${config.GETALLWEBSITETEMPLATES}?type=${type}`);
            if (response && Array.isArray(response)) {
                setTemplates(response);
            } else if (response && response.data && Array.isArray(response.data)) {
                setTemplates(response.data);
            } else {
                setTemplates([]);
            }
        } catch (error) {
            console.error("Error fetching templates:", error);
            setTemplates([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTypeClick = (type) => {
        setActiveType(type);
    };

    const handleCreateClick = () => {
        setFormData({
            name: "",
            description: "",
            templateType: activeType,
            template: "",
            thumbnail: null
        });
        setPreviewImage(null);
        setFormErrors({});
        setShowCreateModal(true);
    };

    const handleEditClick = (template) => {
        setSelectedTemplate(template);
        setFormData({
            name: template.name || "",
            description: template.description || "",
            templateType: template.templateType || activeType,
            template: template.template || "",
            thumbnail: null
        });
        setPreviewImage(template.thumbnailUrl || null);
        setFormErrors({});
        setShowEditModal(true);
    };

    const handleDeleteClick = (templateId) => {
        setItemToDelete(templateId);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (itemToDelete) {
            try {
                await apiService.remove(config.DELETETEMPLATE, `?templateId=${itemToDelete}`);
                await fetchTemplatesByType(activeType);
            } catch (error) {
                console.error("Error deleting template:", error);
            } finally {
                setShowDeleteModal(false);
                setItemToDelete(null);
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setFormData(prev => ({ ...prev, thumbnail: file }));
            setPreviewImage(URL.createObjectURL(file));
            if (formErrors.thumbnail) {
                setFormErrors(prev => ({ ...prev, thumbnail: "" }));
            }
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = "Template name is required";
        if (!formData.templateType) errors.templateType = "Template type is required";
        if (!formData.template.trim()) errors.template = "Template identifier is required";
        if (!showEditModal && !formData.thumbnail) errors.thumbnail = "Thumbnail image is required";
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setBtnLoading(true);
        try {
            const submitData = new FormData();
            submitData.append("Name", formData.name);
            submitData.append("Description", formData.description);
            submitData.append("TemplateType", formData.templateType);
            submitData.append("Template", formData.template);
            if (formData.thumbnail) {
                submitData.append("Thumbnail", formData.thumbnail);
            }

            await apiService.create(config.CREATETEMPLATE, submitData);
            await fetchTemplatesByType(activeType);
            setShowCreateModal(false);
            resetForm();
        } catch (error) {
            console.error("Error creating template:", error);
        } finally {
            setBtnLoading(false);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setBtnLoading(true);
        try {
            const submitData = new FormData();
            submitData.append("Name", formData.name);
            submitData.append("Description", formData.description);
            submitData.append("TemplateType", formData.templateType);
            submitData.append("Template", formData.template);
            if (formData.thumbnail) {
                submitData.append("Thumbnail", formData.thumbnail);
            }

            await apiService.update(`${config.UPDATETEMPLATE}/${selectedTemplate.templateId}`, submitData);
            await fetchTemplatesByType(activeType);
            setShowEditModal(false);
            resetForm();
        } catch (error) {
            console.error("Error updating template:", error);
        } finally {
            setBtnLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: "",
            description: "",
            templateType: activeType,
            template: "",
            thumbnail: null
        });
        setPreviewImage(null);
        setFormErrors({});
        setSelectedTemplate(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        return format(new Date(dateString), "dd MMM, yyyy");
    };

    if (loading) return <div className="d-flex justify-content-center align-items-center min-vh-100"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>;
    if (!user) {
        navigate("/");
        return null;
    }

    return (
        <div id="layout-wrapper">
            <Header User={user} />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Page Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                    <div>
                                        <h4 className="mb-1">Website Templates</h4>
                                        <p className="text-muted mb-0">Manage and customize website templates for your clients</p>
                                    </div>
                                    <button 
                                        className="btn btn-primary" 
                                        onClick={handleCreateClick}
                                    >
                                        <i className="ri-add-line align-bottom me-1"></i> Add New Template
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Template Type Buttons */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body p-3">
                                        <div className="d-flex gap-2">
                                            {TEMPLATE_TYPES.map(type => (
                                                <button
                                                    key={type.key}
                                                    onClick={() => handleTypeClick(type.key)}
                                                    className={`btn btn-${activeType === type.key ? type.color : 'light'} ${activeType === type.key ? '' : 'border'}`}
                                                    style={{ borderRadius: "30px", padding: "8px 20px" }}
                                                >
                                                    <i className={`${type.icon} me-2`}></i>
                                                    {type.label}
                                                    {activeType === type.key && templates.length > 0 && (
                                                        <span className="ms-2 badge bg-white text-dark rounded-pill">
                                                            {templates.length}
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Templates Grid */}
                        <div className="row">
                            {isLoading ? (
                                <div className="col-12 text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Loading templates...</p>
                                </div>
                            ) : templates.length > 0 ? (
                                templates.map((template) => (
                                    <div key={template.templateId} className="col-xl-3 col-lg-4 col-md-6 mb-4">
                                        <div className="card h-100 shadow-sm border-0 rounded-3 overflow-hidden">
                                            <div className="position-relative">
                                                {template.thumbnailUrl ? (
                                                    <img 
                                                        src={template.thumbnailUrl} 
                                                        alt={template.name}
                                                        className="card-img-top"
                                                        style={{ height: "200px", objectFit: "cover", width: "100%" }}
                                                        onError={(e) => { e.target.src = "https://placehold.co/400x200?text=No+Preview"; }}
                                                    />
                                                ) : (
                                                    <div className="bg-light d-flex align-items-center justify-content-center" style={{ height: "200px" }}>
                                                        <i className="ri-image-line fs-1 text-muted"></i>
                                                    </div>
                                                )}
                                                <span className={`position-absolute top-0 end-0 m-2 badge bg-${template.templateType === "B2C" ? "primary" : template.templateType === "B2B" ? "success" : "info"}`}>
                                                    {template.templateType}
                                                </span>
                                            </div>
                                            <div className="card-body">
                                                <h5 className="card-title mb-2">{template.name}</h5>
                                                <p className="card-text text-muted small mb-3">
                                                    {template.description || "No description available"}
                                                </p>
                                                <div className="mb-2">
                                                    <span className="badge bg-secondary">Template ID: {template.template}</span>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <small className="text-muted">
                                                        <i className="ri-calendar-line me-1"></i>
                                                        {formatDate(template.createdAt)}
                                                    </small>
                                                    <div className="btn-group">
                                                        <button 
                                                            className="btn btn-sm btn-primary"
                                                            onClick={() => handleEditClick(template)}
                                                        >
                                                            <i className="ri-pencil-line"></i>
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleDeleteClick(template.templateId)}
                                                        >
                                                            <i className="ri-delete-bin-line"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-12">
                                    <div className="text-center py-5">
                                        <div className="avatar-lg mx-auto mb-4">
                                            <div className="avatar-title bg-light text-primary rounded-circle fs-1">
                                                <i className="ri-layout-line"></i>
                                            </div>
                                        </div>
                                        <h5>No Templates Found</h5>
                                        <p className="text-muted">
                                            No templates available in {activeType} category
                                        </p>
                                        <button className="btn btn-primary" onClick={handleCreateClick}>
                                            <i className="ri-add-line me-1"></i> Add Your First Template
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Create Template Modal */}
            <Modal show={showCreateModal} onHide={() => { setShowCreateModal(false); resetForm(); }} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>Add New Template</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleCreateSubmit}>
                    <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label">Template Name <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    name="name"
                                    className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Modern Travel Agency"
                                />
                                {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                            </div>

                            <div className="col-12">
                                <label className="form-label">Description</label>
                                <textarea
                                    name="description"
                                    className="form-control"
                                    rows="2"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder="Brief description of this template"
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Template Type <span className="text-danger">*</span></label>
                                <select
                                    name="templateType"
                                    className={`form-select ${formErrors.templateType ? 'is-invalid' : ''}`}
                                    value={formData.templateType}
                                    onChange={handleInputChange}
                                >
                                    <option value="B2C">B2C</option>
                                    <option value="B2B">B2B</option>
                                    <option value="B2E">B2E</option>
                                    <option value="Travel">Travel</option>
                                </select>
                                {formErrors.templateType && <div className="invalid-feedback">{formErrors.templateType}</div>}
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Template Identifier <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    name="template"
                                    className={`form-control ${formErrors.template ? 'is-invalid' : ''}`}
                                    value={formData.template}
                                    onChange={handleInputChange}
                                    placeholder="e.g., template_1, template_2"
                                />
                                {formErrors.template && <div className="invalid-feedback">{formErrors.template}</div>}
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Thumbnail Image <span className="text-danger">*</span></label>
                                <input
                                    type="file"
                                    className={`form-control ${formErrors.thumbnail ? 'is-invalid' : ''}`}
                                    accept="image/png, image/jpeg, image/jpg, image/webp"
                                    onChange={handleFileChange}
                                />
                                {formErrors.thumbnail && <div className="invalid-feedback">{formErrors.thumbnail}</div>}
                                <small className="text-muted">Recommended: 400x200px, PNG or JPG</small>
                            </div>

                            {previewImage && (
                                <div className="col-12">
                                    <div className="border rounded p-2 bg-light">
                                        <img src={previewImage} alt="Preview" className="img-fluid rounded" style={{ maxHeight: "150px", objectFit: "contain" }} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" className="btn btn-danger" onClick={() => { setShowCreateModal(false); resetForm(); }}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={btnLoading}>
                            {btnLoading ? <><span className="spinner-border spinner-border-sm me-2"></span>Creating...</> : "Create Template"}
                        </button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* Edit Template Modal */}
            <Modal show={showEditModal} onHide={() => { setShowEditModal(false); resetForm(); }} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Template</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleEditSubmit}>
                    <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label">Template Name <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    name="name"
                                    className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                                    value={formData.name}
                                    onChange={handleInputChange}
                                />
                                {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                            </div>

                            <div className="col-12">
                                <label className="form-label">Description</label>
                                <textarea
                                    name="description"
                                    className="form-control"
                                    rows="2"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Template Type <span className="text-danger">*</span></label>
                                <select
                                    name="templateType"
                                    className={`form-select ${formErrors.templateType ? 'is-invalid' : ''}`}
                                    value={formData.templateType}
                                    onChange={handleInputChange}
                                >
                                    <option value="B2C">B2C</option>
                                    <option value="B2B">B2B</option>
                                    <option value="B2E">B2E</option>
                                    <option value="Travel">Travel</option>
                                </select>
                                {formErrors.templateType && <div className="invalid-feedback">{formErrors.templateType}</div>}
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Template Identifier <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    name="template"
                                    className={`form-control ${formErrors.template ? 'is-invalid' : ''}`}
                                    value={formData.template}
                                    onChange={handleInputChange}
                                    placeholder="e.g., template_1, template_2"
                                />
                                {formErrors.template && <div className="invalid-feedback">{formErrors.template}</div>}
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Thumbnail Image</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    accept="image/png, image/jpeg, image/jpg, image/webp"
                                    onChange={handleFileChange}
                                />
                                <small className="text-muted">Leave empty to keep current image</small>
                            </div>

                            {previewImage && (
                                <div className="col-12">
                                    <div className="border rounded p-2 bg-light">
                                        <img src={previewImage} alt="Preview" className="img-fluid rounded" style={{ maxHeight: "150px", objectFit: "contain" }} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" className="btn btn-secondary" onClick={() => { setShowEditModal(false); resetForm(); }}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={btnLoading}>
                            {btnLoading ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</> : "Save Changes"}
                        </button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <DeleteAlert
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleDelete={handleDeleteConfirm}
            />
        </div>
    );
};

export default WebsiteTemplates;